import { CSSProperties } from 'react';

import { FlexColumn } from '@/components/shared/components/FlexColumn';
import { FlexRow } from '@/components/shared/components/FlexRow';
import { Font } from '@/components/shared/components/Font';
import { Icon } from '@/components/shared/components/Icon';
import { Text } from '@/components/shared/components/Text';
import { useCustomTheme } from '@/hooks/useCustomTheme';

interface ProgressLayoutProps {
  StatusTag: React.ReactNode;
  ProgressBar: React.ReactNode;
  children?: React.ReactNode;
  progressLabel?: string;
}

const Layout = ({ StatusTag, ProgressBar, children, progressLabel = 'PROGRESS' }: ProgressLayoutProps) => {
  return (
    <FlexColumn gridGap={16}>
      <FlexRow width={'100%'}>
        <Font variant="subtitle1" color="title">
          <Text strong tId={progressLabel} />
        </Font>
        {StatusTag}
      </FlexRow>
      <FlexColumn width={'100%'} gridGap={8}>
        {ProgressBar}
        {children}
      </FlexColumn>
    </FlexColumn>
  );
};

const Status = ({
  isActive,
  title,
  subtitle,
  subtitle2,
}: {
  isActive: boolean;
  title: string;
  subtitle?: string;
  subtitle2?: string;
}) => {
  return (
    <FlexRow justifyContent={'flex-start'} maxWidth={'100%'} flexGrow={1}>
      <StatusIcon isActive={isActive} />
      <Font
        variant="body2"
        color="textPrimary"
        display="block"
        style={
          {
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 1,
            WebkitBoxOrient: 'vertical',
          } as CSSProperties
        }
      >
        <Text strong tId={title} />
        {subtitle && (
          <>
            {' - '}
            <Text tId={subtitle} />
          </>
        )}
        {subtitle2 && (
          <>
            {' - '}
            <Text tId={subtitle2} />
          </>
        )}
      </Font>
    </FlexRow>
  );
};

const StatusIcon = ({ isActive }: { isActive: boolean }) => {
  const { palette } = useCustomTheme();

  if (isActive) {
    return (
      <Icon size={18} color={palette.success.main}>
        task_alt
      </Icon>
    );
  }

  return (
    <Icon size={18} color={palette.grey[800]}>
      cancel
    </Icon>
  );
};

const ExpandMoreIcon = () => {
  return (
    <Font variant="caption" color="textSecondary" style={{ display: 'flex' }}>
      <Icon>expand_more</Icon>
    </Font>
  );
};

const HistoryButton = ({ label = 'SEE_PROGRESS_HISTORY_MOBILE' }: { label?: string }) => {
  return (
    <span style={{ textAlign: 'right' }}>
      <Font
        variant="caption"
        color="textPrimary"
        align="right"
        style={{ textDecoration: 'underline', textUnderlineOffset: '2px' }}
      >
        <strong>
          <Text tId={label} />
        </strong>
      </Font>
    </span>
  );
};

export const InvalidStatus = () => {
  const { palette } = useCustomTheme();

  return (
    <FlexRow width={'100%'} height={48}>
      <FlexRow flexShrink={0}>
        <Icon size={18} color={palette.text.secondary}>
          update_disabled
        </Icon>
        <Font variant="body2" color="textSecondary">
          <Text tId={'INVALID_PROGRESS'} />
        </Font>
      </FlexRow>
    </FlexRow>
  );
};

export const NoStatus = () => {
  return (
    <FlexRow height={48}>
      <Font variant="subtitle2" color="textPrimary" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Icon size={18}>timer_off</Icon>
        <Text tId={'NOT_STARTED'} />
      </Font>
    </FlexRow>
  );
};

const ProgressComponents = {
  Layout,
  Status,
  ExpandMoreIcon,
  HistoryButton,
  InvalidStatus,
  NoStatus,
};

export { ProgressComponents };
